import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { Mic, BookOpen, ImagePlus, ArrowRight, Leaf, User, BookMarked, Shield, Lock, Users, Gift, Sparkles } from "lucide-react";
import Footer from "@/components/Footer";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/d578b360-5475-4ea3-b1d5-8845ff06801f/images/40b3ca2313ca0443f50187181b1563ec39cf6de894b4e4229875705ff3751303.png";
const EMOTIONAL_1 = "https://images.unsplash.com/photo-1758691031101-adf06314e289?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxmYW1pbHklMjBsb29raW5nJTIwYXQlMjBvbGQlMjBwaG90b3N8ZW58MHx8fHwxNzc2MDA5OTQyfDA&ixlib=rb-4.1.0&q=75&w=800";
const EMOTIONAL_2 = "https://images.unsplash.com/photo-1609311654594-0bf499107ed9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxncmFuZHBhcmVudCUyMGdyYW5kY2hpbGQlMjBsYXVnaGluZ3xlbnwwfHx8fDE3NzYwMDk5NDJ8MA&ixlib=rb-4.1.0&q=75&w=800";
const EMOTIONAL_3 = "https://images.unsplash.com/photo-1464998857633-50e59fbf2fe6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxncmFuZHBhcmVudCUyMGdyYW5kY2hpbGQlMjBsYXVnaGluZ3xlbnwwfHx8fDE3NzYwMDk5NDJ8MA&ixlib=rb-4.1.0&q=75&w=800";
const TEXTURE_BG = "https://static.prod-images.emergentagent.com/jobs/d578b360-5475-4ea3-b1d5-8845ff06801f/images/d3ac6caca27e9f6a2857d6590b2636ed5a6d580d7004f83d5360369abbce93cb.png";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.15 } }
};

function AnimatedSection({ children, className }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  return (
    <motion.div ref={ref} initial="hidden" animate={isInView ? "visible" : "hidden"} variants={stagger} className={className}>
      {children}
    </motion.div>
  );
}

const features = [
  {
    icon: Mic,
    title: "Talk to Your Assistant.",
    text: "Answer guided, empathetic questions about your life's highest highs and lowest lows. Chronicle listens like a friend."
  },
  {
    icon: BookOpen,
    title: "Auto-Generated Timelines.",
    text: "Our AI instantly drafts your stories and builds a personalized memoir as you speak. Watch chapters form in real-time."
  },
  {
    icon: ImagePlus,
    title: "Add Photos & Share.",
    text: "Upload your favorite pictures and share your digital legacy with family and friends through a beautiful link."
  }
];

const GENRE_COLORS = {
  "Family History": "bg-amber-50 text-amber-700 border-amber-200",
  "Travel": "bg-sky-50 text-sky-700 border-sky-200",
  "Career": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Childhood": "bg-rose-50 text-rose-700 border-rose-200",
  "Love & Relationships": "bg-pink-50 text-pink-700 border-pink-200",
  "Personal Growth": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Adventure": "bg-orange-50 text-orange-700 border-orange-200",
  "Health & Wellness": "bg-teal-50 text-teal-700 border-teal-200",
  "Spirituality": "bg-violet-50 text-violet-700 border-violet-200",
  "Other": "bg-gray-50 text-gray-600 border-gray-200",
};

const StoryCard = ({ story }) => {
  const genreStyle = GENRE_COLORS[story.genre] || GENRE_COLORS["Other"];
  const displayTitle = story.first_chapter_title || story.title;
  return (
    <Link
      to={`/share/${story.share_token}`}
      className="group block bg-white/80 backdrop-blur-sm border border-[#D4C9B8]/40 rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
      data-testid={`public-story-${story.book_id}`}
    >
      {/* Cover image or gradient */}
      <div className="h-44 relative overflow-hidden">
        {story.cover_image ? (
          <img src={story.cover_image} alt={story.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#9FB89F] via-[#7A9B7E] to-[#5A7B5E] flex items-center justify-center">
            <BookMarked className="w-12 h-12 text-white/40" />
          </div>
        )}
        <div className="absolute top-3 left-3">
          <span className={`px-3 py-1 rounded-full text-xs font-sans font-medium border ${genreStyle}`}>
            {story.genre}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <p className="font-sans text-[10px] uppercase tracking-wider text-[#9FB89F] mb-1">{displayTitle}</p>
        <h3 className="font-serif text-lg font-medium text-[#2D3A2D] mb-1 group-hover:text-[#7A9B7E] transition-colors">
          {story.title} <span className="font-sans text-sm font-normal text-[#7A8A7A]">by {story.author_name}</span>
        </h3>
        {(story.social_summary || story.summary) && (
          <p className="font-sans text-sm text-[#7A8A7A] leading-relaxed mb-3" data-testid="story-teaser">
            {story.social_summary || story.summary}
          </p>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-[10px] font-sans text-[#9FB89F]">
            <span data-testid="story-views">{story.views || 0} views</span>
            <span data-testid="story-likes">{story.likes || 0} likes</span>
            <span>{story.shares || 0} shares</span>
          </div>
          <span className="font-sans text-xs text-[#9FB89F]">{story.chapter_count} ch.</span>
        </div>
      </div>
    </Link>
  );
};


const PlatformStats = () => {
  const [stats, setStats] = useState({ stories: 0, events: 0, chapters: 0 });
  useEffect(() => {
    axios.get(`${API}/story/stats/platform`).then(r => setStats(r.data)).catch(() => {});
  }, []);
  return (
    <section className="py-16 bg-[#2D3A2D]" data-testid="platform-stats">
      <div className="max-w-5xl mx-auto px-6 flex flex-wrap justify-center gap-12 md:gap-24">
        {[
          { label: "Stories Created", value: stats.stories },
          { label: "Events Created", value: stats.events },
          { label: "Chapters Created", value: stats.chapters },
        ].map((s, i) => (
          <div key={i} className="text-center">
            <p className="font-serif text-4xl md:text-5xl font-bold text-white">{s.value.toLocaleString()}</p>
            <p className="font-sans text-sm text-[#9FB89F] mt-1 tracking-wide uppercase">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
};


const PublicStoriesGallery = () => {
  const [stories, setStories] = useState([]);
  const [genres, setGenres] = useState([]);
  const [activeGenre, setActiveGenre] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPublicStories = async () => {
      try {
        const params = activeGenre !== "All" ? `?genre=${encodeURIComponent(activeGenre)}` : "";
        const response = await axios.get(`${API}/stories/public${params}`);
        setStories(response.data.stories);
        if (response.data.genres?.length > 0) {
          setGenres(response.data.genres);
        }
      } catch (error) {
        console.error("Failed to fetch public stories:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPublicStories();
  }, [activeGenre]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-3 border-[#7A9B7E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (stories.length === 0 && genres.length === 0) return null;

  return (
    <section className="relative z-10 py-24 md:py-32 bg-[#E5D4C1]/15" data-testid="public-stories-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <AnimatedSection className="text-center mb-12">
          <motion.p variants={fadeUp} className="font-sans text-sm uppercase tracking-[0.2em] text-[#7A9B7E] mb-4">
            Community Stories
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-3xl sm:text-4xl tracking-tight leading-snug font-medium text-[#2D3A2D] mb-3">
            Discover Real Memoirs
          </motion.h2>
          <motion.p variants={fadeUp} className="font-sans text-base text-[#7A8A7A] max-w-lg mx-auto">
            Browse stories shared by our community. Get inspired and start writing your own.
          </motion.p>
        </AnimatedSection>

        {/* Genre filter tabs */}
        {genres.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="genre-filter-tabs">
            <button
              onClick={() => setActiveGenre("All")}
              className={`px-5 py-2 rounded-full text-sm font-sans transition-all ${
                activeGenre === "All"
                  ? "bg-[#4A5D4A] text-white shadow-md"
                  : "bg-white/80 text-[#7A8A7A] border border-[#D4C9B8]/50 hover:bg-[#E5D4C1]/40"
              }`}
              data-testid="genre-tab-all"
            >
              All
            </button>
            {genres.map((genre) => (
              <button
                key={genre}
                onClick={() => setActiveGenre(genre)}
                className={`px-5 py-2 rounded-full text-sm font-sans transition-all ${
                  activeGenre === genre
                    ? "bg-[#4A5D4A] text-white shadow-md"
                    : "bg-white/80 text-[#7A8A7A] border border-[#D4C9B8]/50 hover:bg-[#E5D4C1]/40"
                }`}
                data-testid={`genre-tab-${genre}`}
              >
                {genre}
              </button>
            ))}
          </div>
        )}

        {/* Story grid */}
        {stories.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="public-stories-grid">
            {stories.map((story) => (
              <StoryCard key={story.book_id} story={story} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="font-sans text-sm text-[#7A8A7A]">No public stories in this genre yet. Be the first!</p>
          </div>
        )}
      </div>
    </section>
  );
};

const LandingPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Detect logged-in state to switch CTA labels & destinations (do NOT auto-redirect)
  useEffect(() => {
    const token = localStorage.getItem("session_token");
    if (token) {
      axios.get(`${API}/auth/me`).then(() => setIsLoggedIn(true)).catch(() => setIsLoggedIn(false));
    }
  }, []);

  const startStoryPath = isLoggedIn ? "/app" : "/login";
  const startStoryLabel = isLoggedIn ? "Continue Your Story" : "Create Your Own Story";

  return (
    <div className="min-h-screen bg-[#F8F6F1] relative overflow-hidden" data-testid="landing-page">
      {/* Texture overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-30 z-0">
        <img src={TEXTURE_BG} alt="" loading="lazy" className="w-full h-full object-cover" />
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-[#F8F6F1]/80 backdrop-blur-xl border-b border-[#D4C9B8]/30" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#9FB89F] to-[#7A9B7E] flex items-center justify-center">
              <Leaf className="w-4 h-4 text-white" />
            </div>
            <span className="font-serif text-xl font-medium text-[#2D3A2D]">My Story</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/pricing"
              className="font-sans text-sm text-[#7A8A7A] hover:text-[#2D3A2D] transition-colors hidden sm:inline"
              data-testid="nav-plans"
            >
              Plans
            </Link>
            <Link
              to={startStoryPath}
              className="rounded-full px-6 py-2 bg-[#4A5D4A] text-[#F8F6F1] hover:bg-[#2D3A2D] transition-colors font-sans text-sm font-medium"
              data-testid="nav-start-story"
            >
              {isLoggedIn ? "My Canvas" : "Start Your Story"}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="col-span-1 md:col-span-6"
          >
            <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#7A9B7E] mb-6" data-testid="hero-label">
              AI-Powered Memoir Builder
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-none font-medium text-[#2D3A2D] mb-4" data-testid="hero-headline">
              Your Life Story, Told Beautifully. For Free.
            </h1>
            <p className="font-sans text-lg sm:text-xl font-semibold text-[#7A9B7E] mb-4" data-testid="hero-tagline">
              Create your story within 30 minutes.
            </p>
            <p className="font-sans text-base sm:text-lg leading-relaxed text-[#4A5D4A] mb-4 max-w-lg">
              Chat with your personal AI memoir assistant and watch your memories transform into a beautifully written, shareable life story — completely free.
            </p>
            <p className="font-sans text-sm text-[#7A8A7A] mb-10 max-w-lg" data-testid="freemium-note">
              Free launch offer: AI conversations, story creation, sharing & PDF export. Start now!
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <Link
                to={startStoryPath}
                className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 bg-[#4A5D4A] text-[#F8F6F1] hover:bg-[#2D3A2D] transition-all font-sans font-medium text-base shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                data-testid="hero-cta"
              >
                {startStoryLabel}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/occasions"
                className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 bg-[#7A9B7E] text-white hover:bg-[#5A7B5E] transition-all shadow-md font-sans font-medium text-base hover:-translate-y-0.5"
                data-testid="hero-group-cta"
              >
                <Users className="w-4 h-4" />
                Start a Group Book / Events
              </Link>
              <a
                href="#community-stories"
                className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 border border-[#D4C9B8] text-[#4A5D4A] hover:bg-[#E5D4C1]/40 transition-all font-sans font-medium text-base"
                data-testid="hero-see-example"
              >
                See Examples
              </a>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.2 }}
            className="col-span-1 md:col-span-6"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-[#9FB89F]/10 rounded-3xl organic-blob" />
              <img
                src={HERO_IMAGE}
                alt="An elegant open journal representing your life story"
                className="relative z-10 rounded-2xl shadow-xl w-full max-h-[80vh] object-cover"
                data-testid="hero-image"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Celebrate Together — Collaborative Memory Books */}
      <section className="relative z-10 py-20 md:py-28 bg-gradient-to-b from-[#F8F6F1] to-[#E5D4C1]/20" data-testid="celebrate-together-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <AnimatedSection className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Left — Image cluster */}
            <motion.div variants={fadeUp} className="col-span-1 md:col-span-6 relative">
              <div className="absolute -inset-3 bg-[#9FB89F]/8 rounded-3xl organic-blob" />
              <div className="relative grid grid-cols-5 grid-rows-3 gap-3 h-[360px] md:h-[420px]">
                <div className="col-span-3 row-span-3 rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src="https://images.unsplash.com/photo-1726864055959-edb5bbb633e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxncm91cCUyMGZyaWVuZHMlMjBjZWxlYnJhdGluZyUyMHRvZ2V0aGVyJTIwbWVtb3JpZXMlMjBzY3JhcGJvb2t8ZW58MHx8fHwxNzc2NDI3Mzc2fDA&ixlib=rb-4.1.0&q=85&w=600"
                    alt="A group of friends celebrating together"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    data-testid="collab-image-main"
                  />
                </div>
                <div className="col-span-2 row-span-2 rounded-2xl overflow-hidden shadow-md">
                  <img
                    src={EMOTIONAL_2}
                    alt="Sharing warm memories together"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="col-span-2 row-span-1 rounded-xl bg-[#4A5D4A] flex items-center justify-center p-4 shadow-md">
                  <div className="text-center">
                    <p className="font-serif text-2xl font-medium text-white">Together</p>
                    <p className="font-sans text-[10px] uppercase tracking-[0.15em] text-white/60 mt-0.5">is better</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right — Copy */}
            <motion.div variants={fadeUp} className="col-span-1 md:col-span-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-full bg-[#9FB89F]/20 flex items-center justify-center">
                  <Users className="w-4 h-4 text-[#7A9B7E]" />
                </div>
                <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#7A9B7E] font-medium">New Feature</span>
              </div>

              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-snug font-medium text-[#2D3A2D] mb-5">
                Celebrate Together: Collaborative Memory Books
              </h2>

              <p className="font-sans text-base sm:text-lg leading-relaxed text-[#4A5D4A] mb-8">
                Birthdays, retirements, weddings, or just because — invite friends and family to contribute their favourite memories, photos, and messages into one beautiful shared book.
              </p>

              <div className="space-y-4 mb-10">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#9FB89F]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Gift className="w-4 h-4 text-[#7A9B7E]" />
                  </div>
                  <div>
                    <h4 className="font-sans text-sm font-semibold text-[#2D3A2D] mb-0.5">Host an Occasion</h4>
                    <p className="font-sans text-sm text-[#7A8A7A] leading-relaxed">Create an event, share a magic link, and let guests contribute their stories and photos.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#9FB89F]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkles className="w-4 h-4 text-[#7A9B7E]" />
                  </div>
                  <div>
                    <h4 className="font-sans text-sm font-semibold text-[#2D3A2D] mb-0.5">AI Weaves It Together</h4>
                    <p className="font-sans text-sm text-[#7A8A7A] leading-relaxed">Our AI aggregates every contribution into a cohesive, heartfelt narrative — ready to print.</p>
                  </div>
                </div>
              </div>

              <Link
                to="/occasions"
                className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 bg-[#4A5D4A] text-[#F8F6F1] hover:bg-[#2D3A2D] transition-all font-sans font-medium text-sm shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                data-testid="collab-cta"
              >
                Start a Group Book
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative z-10 bg-[#F8F6F1] py-24 md:py-32" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <AnimatedSection className="text-center mb-16">
            <motion.p variants={fadeUp} className="font-sans text-sm uppercase tracking-[0.2em] text-[#7A9B7E] mb-4">How It Works</motion.p>
            <motion.h2 variants={fadeUp} className="font-serif text-3xl sm:text-4xl tracking-tight leading-snug font-medium text-[#2D3A2D]">Three simple steps to your memoir</motion.h2>
          </AnimatedSection>
          <AnimatedSection className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <motion.div key={i} variants={fadeUp} className="bg-transparent border border-[#D4C9B8] p-8 rounded-xl hover:-translate-y-1 transition-transform duration-300" data-testid={`feature-card-${i}`}>
                <div className="w-12 h-12 rounded-full bg-[#9FB89F]/20 flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-[#7A9B7E]" />
                </div>
                <h3 className="font-serif text-xl sm:text-2xl leading-snug font-medium text-[#2D3A2D] mb-3">{feature.title}</h3>
                <p className="font-sans text-base leading-relaxed text-[#4A5D4A]">{feature.text}</p>
              </motion.div>
            ))}
          </AnimatedSection>
        </div>
      </section>

      {/* Trust & Privacy Banner */}
      <section className="relative z-10 py-16 md:py-20" data-testid="trust-banner">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <motion.div variants={fadeUp} className="bg-[#2D3A2D] rounded-3xl p-10 md:p-14 text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-48 h-48 bg-[#9FB89F]/10 organic-blob" />
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#7A9B7E]/10 organic-blob" style={{ animationDelay: "2s" }} />
              <div className="relative z-10">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#9FB89F]/20 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-[#9FB89F]" />
                  </div>
                  <div className="w-12 h-12 rounded-full bg-[#9FB89F]/20 flex items-center justify-center">
                    <Lock className="w-6 h-6 text-[#9FB89F]" />
                  </div>
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl font-medium text-white mb-4 tracking-tight">
                  You own your story.
                </h2>
                <p className="font-sans text-base sm:text-lg text-white/80 leading-relaxed max-w-2xl mx-auto">
                  Your data is <strong className="text-[#9FB89F]">100% encrypted</strong>, <strong className="text-[#9FB89F]">never sold to third parties</strong>, and <strong className="text-[#9FB89F]">completely private</strong>. Your memories belong to you — always.
                </p>
              </div>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* Public Stories Gallery */}
      <div id="community-stories">
        <PublicStoriesGallery />
      </div>

      {/* Platform Stats Matrix */}
      <PlatformStats />

      {/* Emotional Story Section */}
      <section className="relative z-10 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <AnimatedSection className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-16 items-center mb-24">
            <motion.div variants={fadeUp} className="col-span-1 md:col-span-7">
              <img src={EMOTIONAL_1} alt="Family looking at old photos together" loading="lazy" className="rounded-2xl overflow-hidden shadow-lg object-cover w-full h-[400px]" data-testid="emotional-image-1" />
            </motion.div>
            <motion.div variants={fadeUp} className="col-span-1 md:col-span-5">
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-snug font-medium text-[#2D3A2D] mb-4">Every family has a story worth preserving.</h2>
              <p className="font-sans text-base sm:text-lg leading-relaxed text-[#4A5D4A]">Don't let precious memories fade. My Story helps you capture the moments that matter — from childhood adventures to life-changing milestones — in your own words.</p>
            </motion.div>
          </AnimatedSection>

          <AnimatedSection className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-16 items-center mb-24">
            <motion.div variants={fadeUp} className="col-span-1 md:col-span-5 order-2 md:order-1">
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-snug font-medium text-[#2D3A2D] mb-4">The laughter, the love, the legacy.</h2>
              <p className="font-sans text-base sm:text-lg leading-relaxed text-[#4A5D4A]">Our AI interviewer guides you through a warm, personal conversation. It's like talking to a friend who truly listens — then turns your words into a beautifully written memoir.</p>
            </motion.div>
            <motion.div variants={fadeUp} className="col-span-1 md:col-span-7 order-1 md:order-2">
              <img src={EMOTIONAL_2} alt="Grandparent sharing a tender moment" loading="lazy" className="rounded-2xl overflow-hidden shadow-lg object-cover w-full h-[400px]" data-testid="emotional-image-2" />
            </motion.div>
          </AnimatedSection>

          <AnimatedSection className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-16 items-center">
            <motion.div variants={fadeUp} className="col-span-1 md:col-span-7">
              <img src={EMOTIONAL_3} alt="Friends laughing and sharing memories" loading="lazy" className="rounded-2xl overflow-hidden shadow-lg object-cover w-full h-[400px]" data-testid="emotional-image-3" />
            </motion.div>
            <motion.div variants={fadeUp} className="col-span-1 md:col-span-5">
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-snug font-medium text-[#2D3A2D] mb-4">Share your story with the world.</h2>
              <p className="font-sans text-base sm:text-lg leading-relaxed text-[#4A5D4A]">Generate a beautiful shareable link and let your family, friends, and future generations experience your story through your eyes.</p>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* Social Proof / Stats */}
      <section className="relative z-10 py-20 bg-[#E5D4C1]/30">
        <AnimatedSection className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
          <motion.div variants={fadeUp}>
            <p className="font-serif text-5xl font-medium text-[#2D3A2D] mb-2">100%</p>
            <p className="font-sans text-sm text-[#4A5D4A] uppercase tracking-[0.15em]">Private & Secure</p>
          </motion.div>
          <motion.div variants={fadeUp}>
            <p className="font-serif text-5xl font-medium text-[#2D3A2D] mb-2">Voice</p>
            <p className="font-sans text-sm text-[#4A5D4A] uppercase tracking-[0.15em]">Powered Conversations</p>
          </motion.div>
          <motion.div variants={fadeUp}>
            <p className="font-serif text-5xl font-medium text-[#2D3A2D] mb-2">Real-time</p>
            <p className="font-sans text-sm text-[#4A5D4A] uppercase tracking-[0.15em]">Story Generation</p>
          </motion.div>
        </AnimatedSection>
      </section>

      {/* Footer CTA */}
      <section className="relative z-10 bg-[#4A5D4A] text-[#F8F6F1] py-20 px-6 md:px-12" data-testid="landing-footer-cta">
        <div className="max-w-3xl mx-auto text-center flex flex-col items-center">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="font-serif text-4xl sm:text-5xl font-medium mb-6 tracking-tight">
            Start Your Story
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }} className="font-sans text-base text-[#F8F6F1]/70 mb-10 max-w-md">
            Your memories deserve to be more than memories. Turn them into a legacy.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}>
            <Link to={startStoryPath} className="inline-flex items-center gap-2 rounded-full px-10 py-4 bg-[#F8F6F1] text-[#2D3A2D] hover:bg-white transition-colors font-sans font-medium text-base shadow-lg" data-testid="footer-cta">
              {isLoggedIn ? "Open My Canvas" : "Begin Now"}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Global Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage;
